import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { debounce, skip } from 'rxjs/operators';
import { firstValueFrom, of, timer } from 'rxjs';
import * as R from 'runtypes';

import { AuthService } from 'core';
import { URLS } from 'commons/app.constant';
import { Message, MessageService } from 'services/message/message.service';
import { LocalStorageService } from 'services/local-storage/local-storage.service';
import { AffiliateCtaMessageComponent, AffiliateCtaMessagePayload } from './affiliate-cta-message/affiliate-cta-message.component';

const CTA_MESSAGE_DELAY_MS = 3000;

const R_RegistrationStatus = R.Union(
  R.Literal('NOT_ELIGIBLE'),
  R.Literal('READY_FOR_INVITE'),
  R.Literal('REGISTERED'),
  R.Literal('REGISTERED_WITH_TNC_PENDING'),
);

const R_RegistrationSummaryResponse = R.Record({
  registrationStatus: R_RegistrationStatus,
  lastTncVersionId: R.String.nullable().optional(),
  lastTncUrl: R.String.nullable().optional(),
});
export type RegistrationSummaryResponse = R.Static<typeof R_RegistrationSummaryResponse>;

const R_HoldReason = R.Union(
  R.Literal('NONE'),
  R.Literal('TNC_MISSING'),
  R.Literal('KYC_MISSING'),
  R.Literal('USER_BLOCKED'),
  R.Literal('OTHER'),
);

const R_AffiliateSummaryResponse = R.Record({
  programSummary: R.Record({
    c0FiatRewardPct: R.Number,
    c0CryptoRewardPct: R.Number,
    tierTwoOverride: R.Number,
  }),
  currentBalanceAmtUsdt: R.Number,
  holdReason: R_HoldReason,
  cachedLifetimeRewardSummary: R.Record({
    refereesCount: R.Number,
    rewardAmtUsdt: R.Number,
    directRefereesCount: R.Number,
    directRewardAmtUsdt: R.Number,
    nonDirectRefereesCount: R.Number,
    nonDirectRewardAmtUsdt: R.Number,
  }),
});
export type AffiliateSummaryResponse = R.Static<typeof R_AffiliateSummaryResponse>;

const R_ReferralSummaryResponse = R.Record({
  totalReferralRewardZoomAmt: R.Number,
});
export type ReferralSummaryResponse = R.Static<typeof R_ReferralSummaryResponse>;

const _R_ReferreesSortKey = R.Union(R.Literal('REFERRED_TS'), R.Literal('LIFETIME_REWARD_AMT'));
export type ReferreesSortKey = R.Static<typeof _R_ReferreesSortKey>;

export type GetReferreesParams =
  | {
      sortKey: ReferreesSortKey;
    }
  | { sortKey: 'REFERRED_TS'; maxReferredTs: string }
  | { sortKey: 'LIFETIME_REWARD_AMT'; maxReferredTs: string; maxLifetimeRewardAmt: number };

const R_ReferralRewardStatus = R.Union(R.Literal('PENDING'), R.Literal('GRANTED'), R.Literal('NOT_GRANTED'));
export type ReferralRewardStatus = R.Static<typeof R_ReferralRewardStatus>;

const R_Referree = R.Record({
  zoomMeHandle: R.String.nullable().optional(),
  referredTs: R.String,
  referralRewardStatus: R_ReferralRewardStatus,
  lifetimeRewardAmtUsdt: R.Number,
  nonDirectRefereesCount: R.Number,
});
export type Referree = R.Static<typeof R_Referree>;

const R_ReferreesResponse = R.Record({
  referrees: R.Array(R_Referree),
});

export type GetDepositsParams = {
  maxDepositTs?: string;
};

const R_Deposit = R.Record({
  entryId: R.Number,
  depositTs: R.String,
  amountUsdt: R.Number,
  cycleStartTs: R.String,
  cycleEndTs: R.String,
});
export type Deposit = R.Static<typeof R_Deposit>;

const R_DepositsResponse = R.Record({
  deposits: R.Array(R_Deposit),
});

@Injectable({
  providedIn: 'root',
})
export class AffiliateService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private localService = inject(LocalStorageService);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);

  private ctaMessage: Message | undefined;

  constructor() {
    this.authService.$loggedIn
      .pipe(
        skip(1), // Take only changes after the initial value; avoid spamming the user when screen is refreshed.
        debounce((hasLoggedIn) => (hasLoggedIn ? timer(CTA_MESSAGE_DELAY_MS) : of(undefined))),
      )
      .subscribe({
        next: (hasLoggedIn) => {
          this.dismissCTAMessage();
          if (hasLoggedIn) {
            this.ctaIfNeeded().catch(console.error);
          }
        },
      });
  }

  private dismissCTAMessage() {
    if (this.ctaMessage) {
      this.messageService.dismissMessage(this.ctaMessage);
      this.ctaMessage = undefined;
    }
  }

  public optOutPopups() {
    this.localService.affiliatePopupOptOut = true;
  }

  private async ctaIfNeeded() {
    if (this.localService.affiliatePopupOptOut) {
      return;
    }

    const response = await this.getRegistrationSummary();

    if (response.registrationStatus === 'READY_FOR_INVITE') {
      this.ctaMessage = this.messageService.action(
        'You are welcome to join our affiliate program.',
        undefined,
        {
          type: 'invite',
          tncUrl: response.lastTncUrl!,
          tncVersionId: response.lastTncVersionId!,
        } satisfies AffiliateCtaMessagePayload,
        AffiliateCtaMessageComponent,
        () => this.reviewAffiliateTNC(),
      );
    } else if (response.registrationStatus === 'REGISTERED_WITH_TNC_PENDING') {
      this.ctaMessage = this.messageService.action(
        'We have updated our affiliate program terms.',
        undefined,
        {
          type: 're-tnc',
          tncUrl: response.lastTncUrl!,
          tncVersionId: response.lastTncVersionId!,
        } satisfies AffiliateCtaMessagePayload,
        AffiliateCtaMessageComponent,
        () => this.reviewAffiliateTNC(),
      );
    }
  }

  public async reviewAffiliateTNC() {
    await this.router.navigate(['/settings'], {
      state: { reviewAffiliateTNC: true },
    });
    this.dismissCTAMessage();
  }

  public getRegistrationSummary(): Promise<RegistrationSummaryResponse> {
    return firstValueFrom(this.http.get(URLS.affiliateRegistrationSummaryUrl)).then(R_RegistrationSummaryResponse.check);
  }

  public ackTnc(versionId: string): Promise<void> {
    return firstValueFrom(this.http.post<void>(URLS.affiliateTncAcksUrl(versionId), undefined));
  }

  public getAffiliateSummary(): Promise<AffiliateSummaryResponse> {
    return firstValueFrom(this.http.get(URLS.affiliateSummaryUrl)).then(R_AffiliateSummaryResponse.check);
  }

  public getReferralSummary(): Promise<ReferralSummaryResponse> {
    return firstValueFrom(this.http.get(URLS.affiliateReferralSummaryUrl)).then(R_ReferralSummaryResponse.check);
  }

  public getReferrees(params: GetReferreesParams): Promise<Referree[]> {
    return firstValueFrom(this.http.get(URLS.affiliateReferreesUrl, { params }))
      .then(R_ReferreesResponse.check)
      .then((response) => response.referrees);
  }

  public getReferreesExport(): Promise<HttpResponse<Blob>> {
    return firstValueFrom(
      this.http.get(URLS.affiliateReferreesExportUrl, {
        params: {
          format: 'CSV',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        responseType: 'blob',
        observe: 'response',
      }),
    );
  }

  public getDeposits(params: GetDepositsParams): Promise<Deposit[]> {
    return firstValueFrom(this.http.get(URLS.affiliateDepositsUrl, { params }))
      .then(R_DepositsResponse.check)
      .then((response) => response.deposits);
  }

  public getDepositsExport(): Promise<HttpResponse<Blob>> {
    return firstValueFrom(
      this.http.get(URLS.affiliateDepositsExportUrl, {
        params: {
          format: 'CSV',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        responseType: 'blob',
        observe: 'response',
      }),
    );
  }
}
